import { ref, onMounted } from 'vue'
import { useErrorHandler } from '@/utils/errorHandler'
import { useToast } from 'primevue/usetoast'
import type { ToastMessageOptions } from 'primevue/toast'
import { useI18n } from '@/composables/useI18n'

// Extended toast message with custom data
interface ExtendedToastMessage extends ToastMessageOptions {
  data?: Record<string, unknown>
}

export interface PersistentWarning {
  id: string
  severity: 'warn' | 'info' | 'error'
  summary: string
  detail: string
  data?: Record<string, any>
  createdAt: number
  persistent: boolean
}

const STORAGE_KEY = 'hudstore_persistent_warnings'
const MAX_AGE_MS = 24 * 60 * 60 * 1000 // 24 hours max age

export function usePersistentWarnings() {
  const toast = useToast()
  const { t } = useI18n()
  const warnings = ref<PersistentWarning[]>([])
  const { handleError } = useErrorHandler()

  // Load warnings from localStorage
  const loadWarnings = async () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored) as PersistentWarning[]
        const now = Date.now()

        // Filter out warnings older than MAX_AGE_MS
        warnings.value = parsed.filter(w => now - w.createdAt < MAX_AGE_MS)

        // Update localStorage if we filtered any
        if (warnings.value.length !== parsed.length) {
          await saveWarnings().catch(() => {})
        }
      }
    } catch (error) {
      await handleError(error)
      warnings.value = []
    }
  }

  // Save warnings to localStorage
  const saveWarnings = async () => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(warnings.value))
    } catch (error) {
      await handleError(error)
    }
  }

  // Add a new persistent warning
  const addPersistentWarning = (warning: Omit<PersistentWarning, 'id' | 'createdAt'>) => {
    const newWarning: PersistentWarning = {
      ...warning,
      id: `warning_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: Date.now()
    }

    warnings.value.push(newWarning)
    saveWarnings().catch(() => {})
    
    // Show the toast immediately with sticky behavior
    showPersistentToast(newWarning)
    
    return newWarning.id
  }

  // Remove a warning by ID
  const removeWarning = (id: string) => {
    warnings.value = warnings.value.filter(w => w.id !== id)
    saveWarnings().catch(() => {})
  }

  // Clear all warnings
  const clearAllWarnings = () => {
    warnings.value = []
    saveWarnings().catch(() => {})
  }

  // Show a persistent toast that requires manual dismissal
  const showPersistentToast = (warning: PersistentWarning) => {
    // Cast to any to allow custom data property - PrimeVue supports it at runtime
    // but TypeScript types don't include it
    toast.add({
      severity: warning.severity,
      summary: warning.summary,
      detail: warning.detail,
      life: 0, // 0 means sticky - won't auto-dismiss
      closable: true,
      group: 'persistent',
      data: {
        ...warning.data,
        warningId: warning.id
      }
    } as ExtendedToastMessage)
  }

  // Display all stored warnings as toasts (called on mount)
  const displayStoredWarnings = () => {
    warnings.value.forEach(warning => {
      showPersistentToast(warning)
    })
  }

  // Handle customer creation warnings specifically
  const handleCustomerCreationWarning = (apiResponse: any) => {
    
    if (!apiResponse.warnings) {
      return
    }

    const warnings = Array.isArray(apiResponse.warnings) ? apiResponse.warnings : [apiResponse.warnings]
    
    warnings.forEach((warning: any) => {
      
      if (warning.type === 'duplicate_phone_number' || warning.type === 'duplicate_phone' || warning.phone_exists) {
        const phoneNumber = warning.phone_number || warning.phone
        const existingCustomer = warning.existing_customer || warning.customer
        
        let detail = warning.message || t('customer.warnings.duplicatePhone', {
          phone: phoneNumber,
          customerName: existingCustomer?.name || existingCustomer?.username || 'Unknown'
        })
        
        // Fallback if translation not found
        if (detail.includes('customer.warnings.duplicatePhone')) {
          detail = `Phone number ${phoneNumber} already exists for customer: ${existingCustomer?.name || existingCustomer?.username || 'Unknown'}. This might be an existing customer with new social media accounts.`
        }
        
        // Add recommendation if available
        if (warning.recommendation) {
          detail += `\n\n${warning.recommendation}`
        }

        addPersistentWarning({
          severity: 'warn',
          summary: t('customer.warnings.possibleDuplicate') || 'Possible Duplicate Customer',
          detail,
          persistent: true,
          data: {
            type: 'duplicate_phone_number',
            phone: phoneNumber,
            existingCustomerId: existingCustomer?.id,
            existingCustomerName: existingCustomer?.name || existingCustomer?.username,
            recommendation: warning.recommendation,
            originalMessage: warning.message
          }
        })
      } else if (warning.message) {
        // Generic warning from API
        addPersistentWarning({
          severity: 'warn',
          summary: t('common.warning') || 'Warning',
          detail: warning.message,
          persistent: true,
          data: warning
        })
      }
    })
  }

  // Initialize on mount
  onMounted(() => {
    loadWarnings()
    displayStoredWarnings()
  })

  return {
    warnings,
    addPersistentWarning,
    removeWarning,
    clearAllWarnings,
    handleCustomerCreationWarning,
    displayStoredWarnings
  }
}

// Global instance for app-wide persistent warnings
let globalWarningsInstance: ReturnType<typeof usePersistentWarnings> | null = null

export function useGlobalPersistentWarnings() {
  if (!globalWarningsInstance) {
    globalWarningsInstance = usePersistentWarnings()
  }
  return globalWarningsInstance
}